import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAppointmentStatusTranslationMap, getAppointmentTypeTranslationMap, getEventKindTranslationMap } from 'FRONTEND/components/Calendar/utils';
import CheckboxSelectField from 'FRONTEND/components/CheckboxSelectField/CheckboxSelectField';
import {
  appointmentStatusFilterChange,
  eventKindFilterChange,
  selectAppointmentStatusFilterList,
  selectEventKindFilterList,
  selectTypeFilterList,
  typeFilterChange,
} from 'FRONTEND/reducers/calendarSlice';
import {
  APPOINTMENT_STATUS_FILTER_LIST,
  APPOINTMENT_TYPE_LIST,
  EVENT_KIND_LIST,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

type CalendarFilterFieldProps = {
  isLoading?: boolean;
};

const CalendarFilterField = ({ isLoading }: CalendarFilterFieldProps) => {
  const dispatch = useDispatch();
  const appointmentStatusFilterList = useSelector(selectAppointmentStatusFilterList);
  const typeFilterList = useSelector(selectTypeFilterList);
  const eventKindFilterList = useSelector(selectEventKindFilterList);

  // Unified filter options
  const filterOptions = [
    ...EVENT_KIND_LIST.map(kind => ({
      value: `eventKind_${kind}`,
      label: getEventKindTranslationMap()[kind],
      type: 'eventKind',
      originalValue: kind,
    })),
    ...APPOINTMENT_STATUS_FILTER_LIST.map(status => ({
      value: `appointmentStatus_${status}`,
      label: getAppointmentStatusTranslationMap()[status],
      type: 'appointmentStatus',
      originalValue: status,
    })),
    ...APPOINTMENT_TYPE_LIST.map(type => ({
      value: `appointmentType_${type}`,
      label: getAppointmentTypeTranslationMap()[type],
      type: 'appointmentType',
      originalValue: type,
    })),
  ];

  // Get currently selected values
  const selectedValues = [
    ...(eventKindFilterList || []).map(kind => `eventKind_${kind}`),
    ...(appointmentStatusFilterList || []).map(status => `appointmentStatus_${status}`),
    ...(typeFilterList || []).map(type => `appointmentType_${type}`),
  ];

  const handleChange = (newSelectedValues: string[]) => {
    const newEventKinds: string[] = [];
    const newAppointmentStatuses: string[] = [];
    const newAppointmentTypes: string[] = [];

    newSelectedValues.forEach(value => {
      const option = filterOptions.find(opt => opt.value === value);
      if (!option) {
        return;
      }

      switch (option.type) {
        case 'eventKind':
          newEventKinds.push(option.originalValue);
          break;
        case 'appointmentStatus':
          newAppointmentStatuses.push(option.originalValue);
          break;
        case 'appointmentType':
          newAppointmentTypes.push(option.originalValue);
          break;
      }
    });

    dispatch(eventKindFilterChange({ eventKindFilterList: newEventKinds }));
    dispatch(appointmentStatusFilterChange({ appointmentStatusFilterList: newAppointmentStatuses }));
    dispatch(typeFilterChange({ typeFilterList: newAppointmentTypes }));
  };

  return (
    <CheckboxSelectField
      options={filterOptions}
      selectedValues={selectedValues}
      onChange={handleChange}
      placeholder={i18n._('No filter applied')}
      selectedPlaceholder={i18n._('Filters applied')}
      isLoading={isLoading}
    />
  );
};

export default CalendarFilterField;
